import { CheckIcon } from '@radix-ui/react-icons'
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import ArrowDown from '@/components/ui/icons/arrow-down'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { VariationChoice } from '@/lib/link-variation-options'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import { OutOfStockTag } from './medication-combobox'

/** A short, search-less dropdown styled like MedicationCombobox, for one variation dimension. */
export default function VariationSelect({
  title,
  options,
  value,
  onValueChange,
  placeholder = 'Select an option...',
  isDisabled = false,
  testId
}: {
  title: string
  options: VariationChoice[]
  /** null while nothing is selected; '' is a real value (a variation with no quantity) */
  value: string | null
  onValueChange: (value: string) => void
  placeholder?: string
  isDisabled?: boolean
  testId?: string
}) {
  const [open, setOpen] = useState(false)

  const selectedOption = value === null ? undefined : options.find(option => option.value === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={isDisabled}
          type='button'
          role='combobox'
          aria-expanded={open}
          data-testid={testId}
          className={cn(
            'py-4 px-3 rounded-2xl border border-custom-neutral-200 bg-transparent flex gap-4 items-center justify-between transition-all duration-200 ease-in-out hover:bg-secondary/20 w-full relative',
            isDisabled && 'opacity-50 cursor-not-allowed hover:bg-transparent'
          )}
        >
          <div className='absolute left-4 -top-2 text-xs leading-3 tracking-[0.15px] text-custom-neutral-800 p-1 bg-white rounded-lg'>
            {title}
          </div>

          <span className='text-base font-normal leading-6 tracking-[0.15px] truncate text-left'>
            {selectedOption?.label ?? placeholder}
          </span>

          {selectedOption && !selectedOption.isInStock && <OutOfStockTag />}

          <ArrowDown className='ml-auto shrink-0' />
        </button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-(--radix-popover-trigger-width) p-0 rounded-xl'>
        <Command>
          <CommandList>
            <CommandGroup className='p-0'>
              {options.map(option => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    onValueChange(option.value)
                    setOpen(false)
                  }}
                  className='gap-3 px-4 py-2.5'
                >
                  <span className='truncate text-sm text-custom-neutral-900'>{option.label}</span>
                  <span className='ml-auto flex shrink-0 items-center gap-2'>
                    {!option.isInStock && <OutOfStockTag />}
                    <CheckIcon
                      className={cn('h-4 w-4 shrink-0', value === option.value ? 'opacity-100' : 'opacity-0')}
                    />
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
